import { useMemo, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";
import { toast } from "react-toastify";

import Modal from "components/v2/Modal/Modal";
import Avatar from "components/v2/Avatar/Avatar";
import Button from "components/v2/Button/Button";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check.svg";
import { ReactComponent as DeclinedIcon } from "assets/images/iconsV2/slash-circle-01.svg";
import errorToast from "helpers/errorToast";
import { inviteProvider } from "api";
import testIds from "constants/testIds";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import ProviderInviteStatus from "constants/ProviderInviteStatus";
import { updateBackgroundLoadingState } from "store/store";

import "./InviteProvidersModal.scss";

type ProviderInviteStatusType = typeof ProviderInviteStatus[keyof typeof ProviderInviteStatus];

export type InviteProvider = {
  id: string | number;
  status: ProviderInviteStatusType | string;
  name?: string;
  company_name?: string;
  company_logo?: string;
};

type InviteProviderRowProps = {
  provider: InviteProvider;
  shipmentId: string;
};

type InviteProvidersModalProps = {
  isShowing: boolean;
  close: () => void;
  providers: InviteProvider[];
  shipmentId: string;
};

const ELIGIBLE_STATUSES: ProviderInviteStatusType[] = [ProviderInviteStatus.NOT_INVITED, ProviderInviteStatus.CANCELED];

const getProviderName = (provider: InviteProvider) => provider.name || provider.company_name || "";
const getProviderLogo = (provider: InviteProvider) => provider.company_logo;

const canInviteProvider = (provider: InviteProvider): boolean =>
  ELIGIBLE_STATUSES.includes(provider.status as ProviderInviteStatusType);

const compareProvidersByName = (a: InviteProvider, b: InviteProvider): number =>
  getProviderName(a).localeCompare(getProviderName(b), undefined, { sensitivity: "base" });

const sortProviders = (providers: InviteProvider[]): InviteProvider[] => {
  const uninvited = providers.filter((provider) => provider.status === ProviderInviteStatus.NOT_INVITED);
  const invited = providers.filter((provider) => provider.status !== ProviderInviteStatus.NOT_INVITED);

  return [...uninvited.sort(compareProvidersByName), ...invited.sort(compareProvidersByName)];
};

function InviteProviderRow({ provider, shipmentId }: InviteProviderRowProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });

  const [isLoading, setIsLoading] = useState(false);
  const isDeclinedProvider = provider.status === ProviderInviteStatus.DECLINED;
  const [isInvited, setIsInvited] = useState(!canInviteProvider(provider) && !isDeclinedProvider);

  const providerName = getProviderName(provider);
  const providerLogo = getProviderLogo(provider);

  const onInvite = () => {
    setIsLoading(true);
    inviteProvider(shipmentId, provider.id)
      .then(() => {
        setIsLoading(false);
        setIsInvited(true);
        toast.success(
          intl.formatMessage(
            {
              id: "quote-request.providerInvitedSuccess",
              defaultMessage: "{providerName} has been invited",
            },
            { providerName },
          ),
        );
        actions.updateBackgroundLoadingState(true);
        queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteProviderOptionsListing(shipmentId) });
      })
      .catch((err) => {
        setIsLoading(false);
        errorToast(
          err,
          intl.formatMessage({
            id: "quote-request.couldNotInviteProvider",
            defaultMessage: "Could not invite provider. Please try again later.",
          }),
        );
      });
  };

  return (
    <div className="invite-providers-modal__row">
      <Avatar size="sm" image={providerLogo} name={providerName} />
      <p className="invite-providers-modal__name">{providerName}</p>
      <div className="invite-providers-modal__status">
        {!isLoading && !isInvited && !isDeclinedProvider && (
          <span data-testid={testIds.SHIPMENT_DASHBOARD_INVITE_PROVIDER_BUTTON}>
            <Button
              className="invite-providers-modal__invite-btn"
              variant="tertiary-color"
              size="md"
              iconLeft={<PlusIcon />}
              label={<FormattedMessage id="quote-request.invite" defaultMessage="Invite" />}
              onClick={onInvite}
            />
          </span>
        )}
        {isLoading && <FontAwesomeIcon icon={faCircleNotch} className="text-secondary font-16" spin />}
        {!isLoading && isDeclinedProvider && (
          <div className="invite-providers-modal__declined">
            <DeclinedIcon className="invite-providers-modal__declined-icon" />
            <FormattedMessage
              id="shipment-quotes.components.invitedProviders.declined"
              defaultMessage="Declined"
            />
          </div>
        )}
        {!isLoading && isInvited && (
          <div className="invite-providers-modal__invited">
            <CheckIcon className="invite-providers-modal__invited-icon" />
            <FormattedMessage id="quote-request.invited" defaultMessage="Invited" />
          </div>
        )}
      </div>
    </div>
  );
}

function InviteProvidersModal({ isShowing, close, providers, shipmentId }: InviteProvidersModalProps) {
  const intl = useIntl();

  const sortedProviders = useMemo(() => sortProviders(providers), [providers]);

  return (
    <Modal show={isShowing} onHide={close} size="lg">
      <Modal.Header closeButtonTestId={testIds.SHIPMENT_DASHBOARD_INVITE_PROVIDER_MODAL_CLOSE_BUTTON}>
        <h2 className="invite-providers-modal__title">
          {intl.formatMessage({
            id: "quote-request.selectProvidersToInvite",
            defaultMessage: "Select providers to invite",
          })}
        </h2>
      </Modal.Header>
      <Modal.Body className="invite-providers-modal__body">
        {sortedProviders.length > 0 ? (
          <div className="invite-providers-modal__list">
            {sortedProviders.map((provider) => (
              <InviteProviderRow key={provider.id} provider={provider} shipmentId={shipmentId} />
            ))}
          </div>
        ) : (
          <p className="invite-providers-modal__empty">
            <FormattedMessage
              id="quote-request.noProvidersEligible"
              defaultMessage="No providers are eligible for invitation."
            />
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default InviteProvidersModal;
